import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, CalendarClock, Clock, Moon, UserPlus, UserRoundCheck, XCircle } from 'lucide-react';
import type { PlatformNotification } from '@worki/shared';
import { api } from '../../lib/api.js';
import { currentLocale } from '../../i18n.js';
import { formatDate } from '../../lib/format.js';
import { EmptyState, Spinner } from '../../components/ui.js';
import { Pill, Row } from './parts.js';

const ICONS = {
  pendingApproval: UserRoundCheck,
  pastDue: AlertTriangle,
  trialEnding: Clock,
  subscriptionEnding: CalendarClock,
  cancelled: XCircle,
  dormant: Moon,
  newLead: UserPlus,
} as const;

const TONES = {
  pendingApproval: 'info',
  pastDue: 'bad',
  trialEnding: 'warn',
  subscriptionEnding: 'warn',
  cancelled: 'bad',
  dormant: 'neutral',
  newLead: 'good',
} as const;

/**
 * ADM-004 — what the platform wants a person to look at today.
 *
 * Nothing is marked read, because nothing here was ever written down: each
 * entry is a state the platform is in right now, and it disappears when that
 * state changes rather than when somebody clicks it. A trial that was renewed
 * is not a notification anyone has to dismiss.
 */
export function useNotifications() {
  return useQuery({
    queryKey: ['platform', 'notifications'],
    queryFn: () => api.get<{ items: PlatformNotification[] }>('/platform/notifications'),
  });
}

export function NotificationRow({ item }: { item: PlatformNotification }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();
  const Glyph = ICONS[item.kind];

  const detail = (): string => {
    if ((item.kind === 'trialEnding' || item.kind === 'subscriptionEnding') && item.days !== null) {
      return t('admin.notifications.endsInDays', { count: item.days });
    }
    if (item.kind === 'pendingApproval' && item.days !== null) return t('admin.notifications.waitingDays', { count: item.days });
    if (item.kind === 'dormant') {
      return item.days === null ? t('admin.notifications.neverActive') : t('admin.notifications.silentDays', { count: item.days });
    }
    return item.at ? formatDate(item.at, locale) : '';
  };

  return (
    <Row
      icon={<Glyph className="h-4 w-4" aria-hidden />}
      title={item.name}
      subtitle={detail()}
      meta={<Pill tone={TONES[item.kind]}>{t(`admin.notifications.${item.kind}`)}</Pill>}
      /* A lead has no business to open yet — that is what makes it a lead. */
      {...(item.businessId ? { onClick: () => navigate(`/platform/businesses?open=${item.businessId}`) } : {})}
    />
  );
}

export function NotificationsSection() {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const items = notifications.data?.items ?? [];

  if (notifications.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (items.length === 0) {
    return <EmptyState icon={<Building2 className="h-7 w-7" />} title={t('admin.notifications.empty')} />;
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <NotificationRow key={`${item.kind}-${item.businessId ?? item.name}-${index}`} item={item} />
      ))}
    </div>
  );
}
